<template lang="pug">
    .thumbnail(v-if="edit")
        // progressive-img(:src="src")
        img(:src="src" :style="style")
        // el-button.delete-image(
        //     v-if="edit && isDeletable"
        //     circle type="info" size="mini"
        //     icon="el-icon-fa-trash-alt far"
        //     @click="deleteImage(index)"
        // )
    .thumbnail(v-else)
        img(:src="src" :style="style")
</template>

<script>

import _ from 'lodash'

import { getFitStringFromProcessObject } from '@/helpers'
import { croppedImageUrl } from '@/extensions/generative-media/imageThumbnail'

export default {
    name: 'ImageContentImageField',
    props: {
        section: {
            type: Object,
            required: false
        },
        content: {
            type: Object,
            required: false
        },
        config: {
            type: Object,
            required: false
        },
        image: {
            type: Object,
            required: true
        },
        edit: {
            type: Boolean,
            default: true,
        },
        height: {
            type: Object,
            default: () => {
                return {
                    preview: 80,
                    edit: 150,
                }
            }
        },
        maxWidth: {
            type: Number,
            required: true
        },
        aspectRatio: {
            type: Number
        },
    },
    computed: {
        usesSourcePreview() {
            return !! _.get(this.image, 'source.id')
                && typeof this.image.id === 'string'
                && this.image.id.startsWith('new:')
        },
        baseSrc() {
            const storageRoute = _.trim(`${process.env.VUE_APP_API_ENDPOINT}${process.env.VUE_APP_API_PUBLIC_STORAGE_ROUTE}`, '/')
            const sourceUrl = _.get(this.image, 'url') || _.get(this.image, 'source.url') || ''

            let table = 'images'
            if (this.image.upload) table = 'uploads'

            if (this.usesSourcePreview) {
                return sourceUrl
            }

            return sourceUrl || `${storageRoute}/${table}/${this.image.id}/${this.image.name}`
        },
        dimensions() {

            // IDEA: Focal Point Cropping
            // https://docs.imgix.com/apis/url/size/crop
            //         ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
            // > The width (w) and height (h) parameters determine the resulting image dimensions.
            // > Both of them are required.
            // > Relative horizontal (x), vertical (y), and/or zoom (z) value
            // > will center the image on those coordinates and crop from there.
            // const fit = 'fit=crop&w=400&h=400&fp-x=.5&fp-y=.5fp-z=1'

            let fit = 'fit=crop&crop=focalpoint&x=.5&y=.5&z=1'
            if (this.image.settings && this.image.settings.processing) {
                fit = getFitStringFromProcessObject(this.image.settings.processing)
            } else if (this.image.pivot && this.image.pivot.fit) {
                fit = this.image.pivot.fit;
            }

            const original = fit;

            const parameters = {
                ... _.get(fit.match(/w=(?<w>[0-9]+\.?[0-9]*)/), 'groups'),
                ... _.get(fit.match(/h=(?<h>[0-9]+\.?[0-9]*)/), 'groups'),
            }

            let { w, h } = parameters
            let aspectRatio = this.image.width / this.image.height

            let selectedAspectRatio = false;
            // let arW, arH = false;
            if (this.aspectRatio) {
               selectedAspectRatio = _.cloneDeep(this.aspectRatio);
            }
            // else {
            //     arW = _.get(this.config, 'aspectRatio.width')
            //     arH = _.get(this.config, 'aspectRatio.height')
            // }

            // if (arW && arH) {
            //     aspectRatio = arW / arH

            //     h = 200
            //     w = h * aspectRatio
            //     fit += `&w=${w}&h=${h}`
            // } else
            if (selectedAspectRatio) {
                aspectRatio = selectedAspectRatio;

                h = 200;
                w = h * aspectRatio;
                if (parameters.w && parameters.h) {
                    fit = fit.replace(/w=([0-9]+\.?[0-9]*)/, `w=${w}`).replace(/h=([0-9]+\.?[0-9]*)/, `h=${h}`)
                } else {
                    fit += `&w=${w}&h=${h}`
                }
            } else if (w && h) {
                aspectRatio = w / h

                h = 200
                w = h * aspectRatio
                fit = fit.replace(/w=([0-9]+\.?[0-9]*)/, `w=${w}`).replace(/h=([0-9]+\.?[0-9]*)/, `h=${h}`)
            } else if (! w || ! h) {
                h = 200
                w = h * aspectRatio
                fit += `&h=${h}`
            }

            // No rotation swap here: w/h are stored OUTPUT-absolute (imgix model), so they
            // already carry the final orientation's aspect for any rotate/flip.
            return { w, h, aspectRatio, fit, original }
        },
        // The displayed (active) layer's concrete file — a REAL baked edit (AI edit, or a
        // concrete crop variant). It's a generated file on the CDN, so it shows the actual
        // edit. The synthesized "Original" layer is EXCLUDED: its file is the raw source
        // (no crop), so using it would shadow a DEFERRED crop (settings.processing) — we
        // fall through to the FileController route instead.
        activeVariantUrl() {
            const variants = _.get(this.image, 'variants') || []
            if (!variants.length) return null
            const active = variants.find(variant => variant.active) || variants[0]
            if (!active || _.get(active, 'kind') === 'original') return null
            return _.get(active, 'file.url') || null
        },
        src() {
            if (this.activeVariantUrl) return this.activeVariantUrl
            // Render via FileController (the /images|uploads|files/{id} route + token, which
            // applies ?fit=crop&…+rot/flip) when either:
            //  • a DEFERRED crop/transform exists (settings.processing) — a static source.url
            //    CDN file ignores those query params (processedFile only exists post-save), OR
            //  • there's no static source.url at all — a just-replaced UPLOAD (uploads table)
            //    or LIBRARY pick, whose pixels FileController must serve (a tokenless route
            //    would 401 / show a broken image).
            const sourceUrl = _.get(this.image, 'url') || _.get(this.image, 'source.url')
            if (_.get(this.image, 'settings.processing') || !sourceUrl) {
                const cropped = croppedImageUrl(this.image, this.dimensions.fit)
                if (cropped) return cropped
            }
            const separator = this.baseSrc.includes('?') ? '&' : '?'
            return `${this.baseSrc}${separator}${this.dimensions.fit}`
        },
        style() {
            let height = this.height.preview
            if (this.edit) height = this.height.edit

            let width = height * this.dimensions.aspectRatio

            const hasUsableMaxWidth = Number.isFinite(this.maxWidth) && this.maxWidth > 40
            if (hasUsableMaxWidth && width > this.maxWidth) {
                width = this.maxWidth
                height = width / this.dimensions.aspectRatio
            }

            return {
                height: `${height}px`,
                width: `${width}px`,
            }
        },
        isDeletable() {
            const min = _.get(this.config, 'min')
            const images = _.get(this.content, 'images.length', 0)
            return min === null || min < images
        },
    },
}
</script>

<style lang="sass" scoped>
    .thumbnail img
        background: $color-body
        // The box is sized to the crop's aspect; cover keeps the image from being
        // stretched/skewed if the loaded file's aspect ever differs from the box.
        object-fit: cover
</style>
